<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRideStore } from '../stores/rideStore';
import TopBar from '../components/TopBar.vue';
import RideModal from '../components/rides/RideModal.vue';
import NewRideButton from '../components/rides/NewRideButton.vue';
import NewRideModal from '../components/rides/NewRideModal.vue';
import EmptyState from '../components/rides/EmptyState.vue';
import ErrorState from '../components/rides/ErrorState.vue';
import RidesHeader from '../components/rides/RidesHeader.vue';
import MaintenanceMessage from '../components/MaintenanceMessage.vue';
import type { Ride, DirectionFilter } from '../types/ride';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { useSwipe } from '@vueuse/core';

const store = useRideStore();
const selectedRide = ref<Ride | null>(null);
const showNewRideModal = ref(false);
const contentRef = ref<HTMLElement | null>(null);

const directions: DirectionFilter[] = ['from_bm', 'to_bm'];

useSwipe(contentRef, {
  onSwipeStart: (e) => {
    e.preventDefault();
  },
  onSwipe: (e) => {
    e.preventDefault();
  },
  onSwipeEnd: (e, direction) => {
    const currentIndex = directions.indexOf(store.selectedDirection);
    if (direction === 'left' && currentIndex === 0) {
      store.selectedDirection = 'to_bm';
    } else if (direction === 'right' && currentIndex === 1) {
      store.selectedDirection = 'from_bm';
    }
  },
});

onMounted(() => {
  store.loadRides();
});

function formatDate(dateStr: string): string {
  return format(parseISO(dateStr), 'dd. MMM', { locale: de });
}

function handleRetry() {
  store.clearError();
  store.loadRides();
}

function handleNewRideSuccess() {
  store.loadRides();
}
</script>

<template>
  <div>
    <TopBar />
    <div class="rides-container">
      <RidesHeader />
      
      <div ref="contentRef" class="rides-content">
        <div class="rides-wrapper" :style="{ transform: `translateX(-${store.selectedDirection === 'to_bm' ? 100 : 0}%)` }">
          <div class="rides-page">
            <div v-if="store.isLoading" class="loading-state">
              Lädt Mitfahrgelegenheiten...
            </div>
            
            <ErrorState
              v-else-if="store.error"
              :message="store.error"
              @retry="handleRetry"
            />
            
            <EmptyState
              v-else-if="!store.hasRides"
              :filter="store.selectedFilter"
            />
            
            <div v-else class="rides-list">
              <div
                v-for="ride in store.filteredRides"
                :key="ride.dataid"
                class="ride-card"
                @click="selectedRide = ride"
              >
                <div class="ride-date">{{ formatDate(ride.date) }}</div>
                <div class="ride-route">
                  <template v-if="store.selectedDirection === 'from_bm'">
                    <div class="route-segment">
                      <span class="label">Nach:</span>
                      <span class="value">{{ ride.to }}</span>
                    </div>
                    <div v-if="ride.via" class="route-segment">
                      <span class="label">Über:</span>
                      <span class="value">{{ ride.via }}</span>
                    </div>
                  </template>
                  <template v-else>
                    <div class="route-segment">
                      <span class="label">Von:</span>
                      <span class="value">{{ ride.from }}</span>
                    </div>
                    <div v-if="ride.via" class="route-segment">
                      <span class="label">Über:</span>
                      <span class="value">{{ ride.via }}</span>
                    </div>
                  </template>
                </div>
                <div class="ride-type" :class="ride.ride_type">
                  {{ ride.ride_type === 'offer' ? 'Angebot' : 'Gesuch' }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <NewRideButton @click="showNewRideModal = true" />
      
      <RideModal
        v-if="selectedRide"
        :ride="selectedRide"
        @close="selectedRide = null"
      />
      
      <NewRideModal
        v-if="showNewRideModal"
        @close="showNewRideModal = false"
        @success="handleNewRideSuccess"
      />
      
      <MaintenanceMessage 
        v-if="store.maintenanceMode" 
        message="Unser System befindet sich gerade in der Wartung, bitte später nochmal versuchen" 
      />
    </div>
  </div>
</template>

<style scoped>
.rides-container {
  position: fixed;
  top: 48px;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  background: #f5f5f5;
}

.rides-content {
  height: 100%;
  overflow: hidden;
  touch-action: none;
}

.rides-wrapper {
  height: 100%;
  display: flex;
  transition: transform 0.3s ease;
}

.rides-page {
  min-width: 100%;
  height: 100%;
  overflow-y: auto;
  padding: 0 1rem 120px;
}

.loading-state {
  text-align: center;
  padding: 1rem;
  color: #666;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.rides-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.ride-card {
  background: white;
  padding: 0.75rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  cursor: pointer;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 1rem;
}

.ride-card:hover {
  transform: translateY(-1px);
  transition: transform 0.2s ease;
}

.ride-date {
  color: #666;
  font-size: 0.9rem;
  white-space: nowrap;
}

.ride-route {
  display: flex;
  gap: 1rem;
  overflow: hidden;
  align-items: center;
}

.route-segment {
  display: flex;
  gap: 0.25rem;
  white-space: nowrap;
  align-items: center;
}

.route-segment:not(:last-child)::after {
  content: "→";
  color: #666;
  margin-left: 0.5rem;
}

.label {
  color: #666;
  font-size: 0.9rem;
}

.value {
  color: #960040;
  font-weight: 500;
}

.ride-type {
  font-size: 0.8rem;
  padding: 0.25rem 0.5rem;
  border-radius: 999px;
  white-space: nowrap;
}

.ride-type.offer {
  background: #dcfce7;
  color: #166534;
}

.ride-type.request {
  background: #fee2e2;
  color: #991b1b;
}
</style>